import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { AddFilter, Filter } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import Badge from "@/components/ui/badge";
import SearchInput from "@/components/ui/search-input";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import { map } from "lodash";
import FilterListItem from "./FilterListItem";
import { getFilterLabel } from "@/hooks/useFilterOptions";

const TRANSLATION_KEY = "datatoolbar.filters";

interface Props {
  /** Combined system and custom field filter options (computed by parent) */
  allFilterOptions: DataTableFilter[];
  filters: Filters;
  setFilters: FiltersSetter;
  counter?: number;
  className?: string;
  ignoredColumns?: string[];
  hasActiveFilters?: boolean;
}

const ToolbarFilterButton: React.FC<Props> = ({
  allFilterOptions,
  filters,
  setFilters,
  counter,
  className,
  ignoredColumns = [],
  hasActiveFilters = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

  const visibleFilterOptions = useMemo(
    () =>
      allFilterOptions.filter((option) => !ignoredColumns.includes(option.key)),
    [allFilterOptions, ignoredColumns]
  );

  const filteredFilterOptions = useMemo(() => {
    if (!searchValue.trim()) return visibleFilterOptions;
    return visibleFilterOptions.filter((option) => {
      const label = getFilterLabel(option);
      return label.toLowerCase().includes(searchValue.toLowerCase());
    });
  }, [visibleFilterOptions, searchValue]);

  const hasSubmenu = (filterType: DataTableFilter["filterType"]) => {
    return (
      filterType.type === "map" ||
      filterType.type === "enum" ||
      filterType.type === "customField" ||
      filterType.type === "boolean" ||
      filterType.type === "text"
    );
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchValue("");
      setActiveSubmenu(null);
    }
  };

  const showSearch = visibleFilterOptions.length > 10;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="secondaryNoBg"
          className={cn(
            "border-transparent group cursor-pointer active:!bg-surface-hover-strong gap-1 px-[6px] py-1",
            className,
            isOpen && "bg-surface-focus-selected"
          )}
          data-state={isOpen ? "open" : "closed"}
        >
          <IconContainer
            color="secondary"
            size="5"
            className={cn(
              "group-hover:!text-primary transition-all duration-300 ease-in-out",
              isOpen && "!text-primary"
            )}
          >
            {hasActiveFilters ? <AddFilter /> : <Filter />}
          </IconContainer>
          <div className="flex items-center gap-[6px] cursor-pointer">
            <Text
              as="label"
              variant="bodySecondary"
              className="text-nowrap cursor-pointer"
            >
              {t(`${TRANSLATION_KEY}.title`)}
            </Text>
            {counter && counter > 0 && (
              <Badge
                text={counter.toString()}
                className="py-[1px] px-[2px] min-w-6 rounded-sm"
                textClassName="text-secondary"
              />
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        padding={4}
        className="gap-1 flex flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {showSearch && (
          <div>
            <SearchInput
              value={searchValue}
              onChange={setSearchValue}
              placeholder={t("search")}
              hideIcon={false}
              clearButtonSize="xs"
              clearButtonColor="secondary"
            />
          </div>
        )}
        <div className="flex flex-col overflow-y-auto" style={{ maxHeight: "calc(var(--radix-popover-content-available-height) - 60px - 24px)" }}>
          {map(filteredFilterOptions, (filterOption) => {
            const filterKey = filterOption.key;
            const filterLabel = getFilterLabel(filterOption);
            const hasOptions = hasSubmenu(filterOption.filterType);

            return (
              <FilterListItem
                key={filterKey}
                filterOption={filterOption}
                filterLabel={filterLabel}
                hasOptions={hasOptions}
                isSubmenuOpen={activeSubmenu === filterKey}
                onSubmenuOpen={() => setActiveSubmenu(filterKey)}
                onSubmenuClose={() => setActiveSubmenu(null)}
                onCloseAll={() => handleOpenChange(false)}
                filters={filters}
                setFilters={setFilters}
              />
            );
          })}
          {filteredFilterOptions.length === 0 && (
            <Text variant="bodyTertiary" className="px-3 py-4 text-center">
              {t("tables.emptyState.title")}
            </Text>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ToolbarFilterButton;
